import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Lock, User, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LanguageMultiSelect } from '@/components/auth/LanguageMultiSelect';
import { CountryPhoneInput } from '@/components/auth/CountryPhoneInput';
import { SUPPORTED_LANGUAGES, getGenders, type SignupForm } from '@/lib/signup.constants';
import {
  AuthPasswordToggle,
  AuthSpinner,
  authShellCopy,
  AUTH_FIELD_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  AUTH_SECONDARY_BUTTON_CLASS,
} from '@/features/auth-shell';
import { type CountryData, COUNTRIES } from '@/lib/countries';
import { getPhoneError, getPasswordCriteria, passwordsMatch } from '@/lib/signup.utils';
import { useLanguages } from '@/hooks/use-languages';
import type { translations } from '@/translations/translations';

type TranslationKeys = typeof translations.fr;

const ICON_CLASS = 'pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground';

interface SignupStep2Props {
  form: SignupForm;
  onUpdate: (fields: Partial<SignupForm>) => void;
  t: TranslationKeys;
  lang: string;
  loading: boolean;
  onBack: () => void;
  geo?: { countryName: string; iso: string; dial: string; source: 'timezone' | 'ip' | 'language' } | null;
}

export const SignupStep2 = ({ form, onUpdate, t, lang, loading, onBack, geo }: SignupStep2Props) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { activeLanguages, refetch: refetchLanguages } = useLanguages();

  useEffect(() => {
    void refetchLanguages();
  }, [refetchLanguages]);

  const languageOptions = useMemo(() => {
    if (activeLanguages.length === 0) return SUPPORTED_LANGUAGES;

    return activeLanguages.map((language) => ({
      value: language.code,
      label: language.displayName || language.name || language.code,
      flag: language.flag || '🌍',
    }));
  }, [activeLanguages]);

  const countryDigits = useMemo(() => {
    const found = COUNTRIES.find((c) => c.name === form.country);
    return found?.digits;
  }, [form.country]);

  const phoneError = useMemo(() => getPhoneError(form.phoneNumber, lang as 'fr' | 'en', countryDigits), [form.phoneNumber, lang, countryDigits]);
  const passwordCriteria = useMemo(() => getPasswordCriteria(form.password), [form.password]);
  const confirmMatch = useMemo(() => passwordsMatch(form.password, form.confirmPassword), [form.password, form.confirmPassword]);
  const confirmTouched = form.confirmPassword.length > 0;

  const genders = getGenders(t);

  return (
    <div className="space-y-7" data-testid="signup-step-2">
      <div className="space-y-5">
        {/* Phone + Country */}
        <div className="space-y-2">
          <Label htmlFor="signup-phone" className="text-sm font-medium text-foreground">
            {t.phoneNumberOptional}
          </Label>
          <CountryPhoneInput
            country={form.country}
            phone={form.phoneNumber}
            onCountryChange={(c: CountryData) => onUpdate({ country: c.name })}
            onPhoneChange={(p) => onUpdate({ phoneNumber: p })}
            countryPlaceholder={t.country}
            phonePlaceholder={t.phoneNumberPlaceholder}
            phoneInputId="signup-phone"
          />
          {phoneError && <p className="text-xs text-danger">{phoneError}</p>}
        </div>

        {/* Gender */}
        <div className="space-y-2">
          <Label htmlFor="gender" className="text-sm font-medium text-foreground">
            {t.gender} <span className="text-brand" aria-hidden="true">*</span>
          </Label>
          <Select value={form.gender} onValueChange={(v) => onUpdate({ gender: v })}>
            <SelectTrigger id="gender" className={AUTH_FIELD_CLASS}>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
                <SelectValue placeholder={t.gender} />
              </div>
            </SelectTrigger>
            <SelectContent>
              {genders.map((g) => (
                <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Password */}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-medium text-foreground">
            {t.password} <span className="text-brand" aria-hidden="true">*</span>
          </Label>
          <div className="relative">
            <Lock className={ICON_CLASS} strokeWidth={1.75} />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              value={form.password}
              onChange={(e) => onUpdate({ password: e.target.value })}
              required
              className={`pl-10 pr-11 ${AUTH_FIELD_CLASS}`}
            />
            <AuthPasswordToggle visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>
          {form.password.length > 0 && (
            <ul className="grid grid-cols-1 gap-x-4 gap-y-1 pt-1 sm:grid-cols-2" aria-live="polite">
              {([
                [passwordCriteria.minLength, t.pwdMinLength],
                [passwordCriteria.hasUppercase, t.pwdUppercase],
                [passwordCriteria.hasLowercase, t.pwdLowercase],
                [passwordCriteria.hasDigit, t.pwdDigit],
                [passwordCriteria.hasSpecial, t.pwdSpecial],
              ] as const).map(([met, label]) => (
                <li key={label} className="flex items-center gap-1.5 text-xs">
                  {met ? (
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 shrink-0 text-faint-foreground" />
                  )}
                  <span className={met ? 'text-success' : 'text-muted-foreground'}>{label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Confirm */}
        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
            {t.confirmPassword} <span className="text-brand" aria-hidden="true">*</span>
          </Label>
          <div className="relative">
            <Lock className={ICON_CLASS} strokeWidth={1.75} />
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              value={form.confirmPassword}
              onChange={(e) => onUpdate({ confirmPassword: e.target.value })}
              required
              aria-invalid={confirmTouched && !confirmMatch ? true : undefined}
              className={`pl-10 pr-16 ${AUTH_FIELD_CLASS} ${
                confirmTouched && !confirmMatch
                  ? 'border-danger/60 focus-visible:border-danger/80 focus-visible:ring-danger/10'
                  : confirmTouched && confirmMatch
                  ? 'border-success/50 focus-visible:border-success/70 focus-visible:ring-success/10'
                  : ''
              }`}
            />
            <AuthPasswordToggle
              visible={showConfirmPassword}
              onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
              className={confirmTouched ? 'right-9' : undefined}
            />
            {confirmTouched && (
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                {confirmMatch
                  ? <CheckCircle2 className="w-4 h-4 text-success" />
                  : <XCircle className="w-4 h-4 text-danger" />
                }
              </div>
            )}
          </div>
          {confirmTouched && !confirmMatch && (
            <p className="text-xs text-danger">{t.passwordsDoNotMatch}</p>
          )}
        </div>
      </div>

      {/* Linguistic Profile */}
      <section className="space-y-4 border-t border-border/70 pt-6" aria-labelledby="signup-languages-title">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand">
              {t.linguisticProfileLabel}
            </p>
            <h2 id="signup-languages-title" className="mt-2 text-lg font-semibold tracking-[-0.02em] text-foreground">
              {t.languagePreferences}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {t.linguisticProfileDesc}
            </p>
          </div>

          <dl className="flex shrink-0 gap-5 sm:pt-1 sm:text-right">
            <div>
              <dd className="text-lg font-semibold tabular-nums text-foreground">{form.spokenLanguages.length}</dd>
              <dt className="text-[0.68rem] uppercase tracking-[0.12em] text-faint-foreground">{t.spokenShort}</dt>
            </div>
            <div>
              <dd className="text-lg font-semibold tabular-nums text-foreground">{form.contributionLanguages.length}</dd>
              <dt className="text-[0.68rem] uppercase tracking-[0.12em] text-faint-foreground">{t.contributionShort}</dt>
            </div>
          </dl>
        </div>

        <div className="grid grid-cols-1 gap-5">
          <LanguageMultiSelect
            label={`${t.spokenLanguages} *`}
            placeholder={t.selectLanguagesSpoken}
            emptyLabel={t.noLanguageSelected}
            selectedSuffix={t.xSelected}
            searchPlaceholder={t.searchLanguage}
            emptySearchLabel={t.noLanguageFound}
            options={languageOptions}
            values={form.spokenLanguages}
            onToggle={(value) => {
              const exists = form.spokenLanguages.includes(value);
              onUpdate({
                spokenLanguages: exists
                  ? form.spokenLanguages.filter((l) => l !== value)
                  : [...form.spokenLanguages, value],
              });
            }}
          />
          <LanguageMultiSelect
            label={`${t.contributionLanguages} *`}
            placeholder={t.selectLanguagesContribute}
            emptyLabel={t.noLanguageSelected}
            selectedSuffix={t.xSelected}
            searchPlaceholder={t.searchLanguage}
            emptySearchLabel={t.noLanguageFound}
            options={languageOptions}
            values={form.contributionLanguages}
            onToggle={(value) => {
              const exists = form.contributionLanguages.includes(value);
              onUpdate({
                contributionLanguages: exists
                  ? form.contributionLanguages.filter((l) => l !== value)
                  : [...form.contributionLanguages, value],
              });
            }}
          />
        </div>
      </section>

      {/* Terms */}
      <div className="rounded-2xl border border-border/70 bg-muted/30 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t.communityLicense}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {t.termsAccept}{' '}
          <span className="font-medium text-foreground">{t.nwuliteLicense}</span>.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className={`${AUTH_SECONDARY_BUTTON_CLASS} px-4`}
        >
          <span className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span>{t.previousStep}</span>
          </span>
        </Button>

        <Button
          type="submit"
          disabled={loading}
          className={`${AUTH_PRIMARY_BUTTON_CLASS} sm:flex-1`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <AuthSpinner />
              <span>{authShellCopy(lang).creatingAccount}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span>{t.joinCommunity}</span>
              <ArrowRight className="w-4 h-4" />
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};
